import React from 'react';
import { ChevronLeft, ChevronRight, Maximize2, Minimize2 } from 'lucide-react';
import { todayISO } from '../../lib/utils';
import { useT } from '../../context/LanguageContext';
import { MONTHS, iso } from './openingHelpers';
import {
  CalendarRoot, CalendarMonth, CalendarLegend,
} from '../../components/calendar/PlannerCalendar';

/** The month a buffet runs in: small it is a date picker, expanded a roster, and it outlines a pending repeat. */
export default function BuffetMonthCalendar({
  month, onMonth, selected, onSelect, days,
  expanded = false, onToggleExpanded,
  // ISO dates a pending repeat would open on, as a Set.
  highlight = null,
}) {
  const { t } = useT();
  const first = new Date(`${month}-01T00:00:00`);
  const y = first.getFullYear();
  const m = first.getMonth();
  const count = new Date(y, m + 1, 0).getDate();
  const today = todayISO();

  const step = (delta) => {
    const nd = new Date(y, m + delta, 1);
    onMonth(`${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, '0')}`);
  };

  const marked = highlight && highlight.size > 0;

  /* Which of the month's days a buffet is actually on — the count the header
     carries, so "is there anything on this month" is answered without reading
     28 cells. */
  let openDays = 0;
  let liveDays = 0;
  for (let d = 1; d <= count; d += 1) {
    const info = days[iso(new Date(y, m, d))];
    if (info?.open > 0) openDays += 1;
    if (info?.running > 0) liveDays += 1;
  }

  const dayInfo = (key) => {
    const info = days[key];
    const names = info?.names || [];
    return {
      /* A day the buffet is ON is filled, the same green as everywhere else.
         The pending repeat is a PROPOSAL, so it gets the dot rather than the
         fill: it is not open until the repeat is saved. */
      on: (info?.open || 0) > 0,
      dot: !((info?.open || 0) > 0) && !!(highlight && highlight.has(key)),
      live: (info?.running || 0) > 0,
      badge: (info?.open || 0) > 1 ? info.open : null,
      selected: key === selected,
      title: names.length
        ? names.map((n) => n.name).join(' · ')
        : (highlight && highlight.has(key) ? t('buffet.month.willOpen') : undefined),
      label: names.length ? `${key} — ${names.map((n) => n.name).join(', ')}` : key,
    };
  };

  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="mb-2 flex items-center justify-between">
        <button type="button" onClick={() => step(-1)} aria-label={t('buffet.month.prev')}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
          <ChevronLeft size={16} />
        </button>
        <div className={`font-semibold text-foreground ${expanded ? 'text-base' : 'text-sm'}`}>
          {MONTHS[m]} {y}
        </div>
        <div className="flex items-center gap-0.5">
          <button type="button" onClick={() => step(1)} aria-label={t('buffet.month.next')}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
            <ChevronRight size={16} />
          </button>
          {onToggleExpanded && (
            /* Bigger, not full-screen: the day's buffets stay beside it, since picking a date is for reading what is on it. */
            <button type="button" onClick={onToggleExpanded}
              aria-pressed={expanded}
              title={expanded ? t('buffet.month.shrink') : t('buffet.month.expand')}
              aria-label={expanded ? t('buffet.month.shrink') : t('buffet.month.expand')}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
              {expanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
          )}
        </div>
      </div>

      {/* The month itself is the shared planner grid — same green for "open",
          same weekend band, same today underline as every other calendar. */}
      <CalendarRoot className={expanded ? 'rbxcal-lg' : ''}>
        <CalendarMonth
          className="is-bare"
          hideHeader
          month={first}
          todayIso={today}
          dayInfo={dayInfo}
          onDayClick={(key) => onSelect(key)}
        />
      </CalendarRoot>

      {expanded && (
        /* Expanded, the calendar is being READ rather than clicked through, so
           it is worth naming what is on each day it opens. */
        <div className="mt-2 space-y-1">
          {Object.keys(days).filter((k) => k.startsWith(month) && days[k]?.names?.length)
            .sort()
            .map((k) => (
              <div key={k} className="flex items-start gap-2 text-xs">
                <span className="w-8 shrink-0 font-semibold tabular-nums text-foreground">
                  {Number(k.slice(-2))}
                </span>
                <span className="flex flex-wrap gap-1">
                  {days[k].names.map((n, ix) => (
                    <span key={ix}
                      className={`rounded px-1.5 py-0.5 text-[10px] leading-tight ${
                        n.status === 'RUNNING' ? 'bg-orange-100 font-semibold text-orange-700'
                          : n.status === 'CLOSED' ? 'bg-muted text-muted-foreground'
                            : 'bg-primary/10 text-foreground'}`}>
                      {n.name}
                    </span>
                  ))}
                </span>
              </div>
            ))}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <button type="button" onClick={() => { onSelect(today); onMonth(today.slice(0, 7)); }}
          className="rounded-md px-2 py-1.5 text-xs font-medium text-primary hover:bg-primary/10">
          {t('buffet.month.jumpToday')}
        </button>
        <CalendarRoot>
          <CalendarLegend items={[
            { key: 'open', label: t('buffet.month.legendOpen', { n: openDays }) },
            ...(liveDays ? [{ key: 'live', label: t('buffet.month.legendLive', { n: liveDays }) }] : []),
            ...(marked ? [{ key: 'dot', label: t('buffet.month.legendRepeat', { n: highlight.size }) }] : []),
          ]} />
        </CalendarRoot>
      </div>
    </div>
  );
}
